<template>
  <div ref="mainContainer" class="batcharchive">
    <div v-if="!Boolean(datafiles)" class="batcharchive-content loading">
      <span class="icon is-large">
        <i class="fa-solid fa-spinner fa-spin fa-2x" />
      </span>
      <span v-if="showLoaderHelpText" class="tag is-primary is-medium mt-5">
        It might take a while if you've selected larger {{ modelName }}!
      </span>
    </div>
    <div v-else class="batcharchive-content">
      <div
        v-if="Object.keys(archiveResponse).length || showErrorTicker"
        class="batcharchive-notifications"
      >
        <div v-if="showErrorTicker" class="notification is-danger">
          <span class="icon">
            <i class="fa-solid fa-bug" />
          </span>
          <span class="text has-text-weight-semibold">
            {{ showErrorTicker }}
          </span>
        </div>
        <div
          v-if="Object.keys(archiveResponse).length"
          class="notification is-fullwidth"
          :class="[archiveResponse.success ? 'is-success' : 'is-warning']"
        >
          <button class="delete" @click="closeNotification()"></button>
          <span class="icon">
            <i class="fa-solid" :class="[success ? 'fa-check' : 'fa-times']" />
          </span>
          <span class="text has-text-weight-semibold">
            {{ archiveResponse.message }}
          </span>
        </div>
      </div>

      <ToggleSection
        v-if="showRelatedObjects"
        class="mb-4"
        :collapsed="!selectedItemsPanelIsOpened"
        :label="selectedItemsLabel"
      >
        <ol id="related-objects" class="related-object-container">
          <template v-for="(item, index) in selectedItems" :key="index">
            <li
              v-if="
                item.number_of_datasets
                  ? item.number_of_datasets.value > 0
                  : true
              "
            >
              <div class="is-flex">
                <FieldComponent
                  :field-meta="nameMetaModel"
                  :data="item.name"
                  style="max-width: 100%"
                  link-behavior="blank"
                />
                <span v-if="item.number_of_datasets" class="ml-2">
                  {{ `(${item.number_of_datasets.value} datasets)` }}
                </span>
                <p
                  v-if="item.archives.value.length"
                  class="tag is-warning ml-2"
                >
                  {{
                    `${
                      relatedObjectsArchiveStatusById[item.id.value]
                    } datafiles in this ${pluralize(
                      modelName,
                      1,
                    )} have been archived before`
                  }}
                </p>
              </div>
            </li>
          </template>
        </ol>
      </ToggleSection>

      <ToggleSection
        v-if="showRelatedObjects && studiesAssociatedToSelectedProjects.length"
        class="mb-4"
        :collapsed="true"
        :label="studiesLabel"
      >
        <ol id="related-objects" class="related-object-container">
          <li
            v-for="study in studiesAssociatedToSelectedProjects"
            :key="study.value.id"
          >
            <div class="is-flex">
              <FieldComponent
                :field-meta="nameMetaModel"
                :data="study"
                link-behavior="blank"
              />
            </div>
          </li>
        </ol>
      </ToggleSection>

      <ToggleSection :collapsed="false" :label="datasetsLabel">
        <div id="datasets">
          <div
            class="content-item-list-container"
            style="width: 100%; flex-grow: 1; margin-bottom: 1rem"
          >
            <table class="table is-fullwidth is-bordered">
              <thead class="sticky at-top">
                <tr>
                  <th>Dataset</th>
                  <th style="border-right: none">Datafile</th>
                  <th class="size-sticky-col">Size</th>
                  <th class="has-text-centered status-sticky-col">Status</th>
                  <th class="has-text-centered archivable-sticky-col">
                    Archivable
                  </th>
                </tr>
              </thead>
              <tfoot class="sticky at-bottom">
                <tr>
                  <th
                    class="has-text-left total-size-sticky"
                    colspan="2"
                    style="border-right: none"
                  >
                    Total Size
                  </th>
                  <th
                    class="has-text-left archivable-sticky-col has-background-white"
                    colspan="3"
                  >
                    <span>
                      {{ prettySize(totalArchivableSize) }}
                    </span>
                  </th>
                </tr>
              </tfoot>
              <tbody>
                <template
                  v-for="(group, groupIndex) in displayedDatafilesInfo"
                  :key="`group-${groupIndex}`"
                >
                  <tr
                    v-for="(datafile, datafileIndex) in group.datafiles"
                    :key="datafile.id"
                  >
                    <td
                      v-if="datafileIndex === 0"
                      :rowspan="group.datafiles.length"
                      class="is-vcentered dataset-col"
                    >
                      <FieldComponent
                        :field-meta="nameMetaModel"
                        :data="group.dataset"
                        :class="{
                          'non-archivable':
                            !datasetsArchivableById[group.dataset.value.id],
                        }"
                        link-behavior="blank"
                      />
                    </td>
                    <td class="is-vcentered" style="border-right: none">
                      <FieldComponent
                        :field-meta="nameMetaModel"
                        :data="datafile.complexName"
                        class="table-cell"
                        :class="{
                          'non-archivable':
                            !datafilesStatusById[datafile.id].archivable ||
                            (datafile.archives.length > 0 &&
                              archiveInfo.ignoreArchivedFiles),
                        }"
                        link-behavior="blank"
                      />
                    </td>
                    <td
                      class="is-vcentered size-sticky-col has-background-white"
                    >
                      <span
                        :class="{
                          'non-archivable':
                            !datafilesStatusById[datafile.id].archivable ||
                            (datafile.archives.length > 0 &&
                              archiveInfo.ignoreArchivedFiles),
                        }"
                      >
                        {{ prettySize(datafile.size || 0) }}
                      </span>
                    </td>
                    <td
                      class="is-vcentered has-text-centered status-sticky-col has-background-white"
                    >
                      <div class="status-container">
                        <div
                          v-if="!datafile.exists"
                          v-tooltip="'Datafile does not exist on disk!'"
                          class="item-status-icon left"
                        >
                          <span class="icon has-text-grey">
                            <i class="fa-solid fa-file-excel" />
                          </span>
                        </div>
                        <div
                          v-if="datafile.archives.length"
                          v-tooltip="
                            `<p>Already archived in</p>
                            ${datafile.archives
                              .map((archive) => `- ${archive.name}`)
                              .join('<br />')}`
                          "
                          class="item-status-icon right"
                        >
                          <span class="icon has-text-grey">
                            <i class="fa-solid fa-file-archive" />
                          </span>
                        </div>
                      </div>
                    </td>
                    <td
                      class="is-vcentered has-text-centered archivable-sticky-col has-background-white"
                    >
                      <div class="is-flex is-justify-content-center">
                        <div
                          v-if="
                            !datafilesStatusById[datafile.id].archivable &&
                            datafilesStatusById[datafile.id].icon.includes(
                              'bug',
                            )
                          "
                          v-tooltip="datafilesStatusById[datafile.id].reason"
                          class="item-status-icon"
                        >
                          <span class="icon has-text-danger">
                            <i class="fa-solid fa-bug" />
                          </span>
                        </div>
                        <div v-else class="item-status-icon">
                          <span
                            class="icon"
                            :class="
                              datafilesStatusById[datafile.id].archivable
                                ? 'has-text-success'
                                : 'has-text-grey'
                            "
                          >
                            <i
                              class="fa-solid"
                              :class="
                                datafilesStatusById[datafile.id].archivable
                                  ? 'fa-check'
                                  : 'fa-times'
                              "
                            />
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </ToggleSection>

      <div class="archive-form mt-5">
        <div class="is-flex is-flex-direction-row">
          <h4 class="title is-4 has-text-left">Archive Information</h4>
          <span class="ml-2 mt-2 is-size-7 is-italic">
            (
            <span class="has-text-danger">*</span>
            indicates a required field)
          </span>
        </div>
        <div class="field">
          <div class="field-body is-horizontal">
            <div class="field">
              <p class="control is-expanded">
                <label class="label">
                  Name
                  <span class="has-text-danger">*</span>
                </label>
                <input
                  v-model="archiveInfo.name"
                  class="input"
                  type="text"
                  placeholder="Name of the archive"
                />
              </p>
            </div>
            <div class="field">
              <p class="control is-expanded">
                <label class="label">
                  Billing Details
                  <span class="has-text-danger">*</span>
                </label>
                <input
                  v-model="archiveInfo.billingDetails"
                  class="input"
                  type="text"
                  placeholder="Budget Number"
                />
              </p>
            </div>
          </div>
        </div>
        <div class="field">
          <label class="label">Description</label>
          <div class="control">
            <textarea
              v-model="archiveInfo.description"
              class="textarea"
              placeholder="Information about the archive"
              rows="4"
            ></textarea>
          </div>
        </div>
        <div class="field">
          <div class="control">
            <label class="checkbox">
              <input
                v-model="archiveInfo.ignoreArchivedFiles"
                type="checkbox"
              />
              <span class="ml-1">
                Ignore datafiles which are already backed-up/scheduled for a
                backup
              </span>
            </label>
          </div>
        </div>
        <div class="content mt-5 has-text-grey">
          <blockquote>
            <ol style="margin-top: 0">
              <li>Tape space will be billed to your group</li>
              <li>
                Archiving is an asynchronous operation that can take up to a few
                days to complete
              </li>
              <!-- <li>The process completeness can be monitored in the job page</li> -->
              <li>
                Once the archive is finished, deletion options will be available
              </li>
            </ol>
            <div class="control ml-3">
              <label class="checkbox">
                <input v-model="archiveConfirmation" type="checkbox" />
                <span class="ml-1">
                  I understand and confirm the above information is being
                  cross-checked!
                  <span class="has-text-danger">*</span>
                </span>
              </label>
            </div>
          </blockquote>
        </div>
        <div class="buttons is-right">
          <div v-tooltip="buttonTooltipMessage" class="mt-3 mb-5">
            <button
              :class="{ 'is-loading': isArchiving }"
              :disabled="
                showErrorTicker ||
                !archiveConfirmation ||
                !archiveInfo.name ||
                !archiveInfo.billingDetails ||
                isArchiving
              "
              class="button is-primary"
              @click="handleArchive()"
            >
              Archive
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { capitalize } from 'lodash-es'
import pluralize from 'pluralize'
import { mapGetters } from 'vuex'
import ToggleSection from '@/components/fields/ToggleSection.vue'
import { post } from '@/api'
import { baseUrl } from '@/../app.config'
import { prettySize } from '@/utils'

export default {
  name: 'BatchArchive',
  components: {
    ToggleSection,
  },
  provide() {
    return { fieldPage: 'list' }
  },
  props: {
    appName: {
      type: String,
      required: true,
    },
    modelName: {
      type: String,
      required: true,
    },
    modelType: {
      type: String,
      required: true,
    },
    datafiles: {
      type: Array,
      required: false,
    },
    selectedItems: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      archiveInfo: {
        name: '',
        description: '',
        billingDetails: '',
        ignoreArchivedFiles: false,
      },
      isArchiving: false,
      archiveConfirmation: false,
      archiveResponse: {},
      showLoaderHelpText: false,
    }
  },
  computed: {
    ...mapGetters('meta', ['getMeta']),
    ...mapGetters('auth', { user: 'loggedInUser' }),
    meta() {
      return this.getMeta(this.appName, this.modelName, this.modelType)
    },
    nameMetaModel() {
      return this.meta.model.attributes.find((x) => x.name === 'name')
    },
    selectedItemsCount() {
      if (this.selectedItems?.[0].number_of_datasets) {
        return this.selectedItems.reduce((count, item) => {
          return item.number_of_datasets.value > 0 ? ++count : count
        }, 0)
      } else {
        return this.selectedItems.length
      }
    },
    displayedDatafilesInfo() {
      const datafilesGroupedByDataset = Array.from(
        this.groupByDataset(this.datafiles || []).values(),
      )

      return datafilesGroupedByDataset.map((group) => ({
        dataset: {
          category: 'property',
          name: 'name',
          value: {
            app_name: 'data_management',
            id: group.dataset.id,
            model_name: 'dataset',
            mode_type: 'DEFAULT',
            name: group.dataset.name,
          },
        },
        datafiles: group.datafiles.map((datafile) => ({
          complexName: {
            category: 'property',
            name: 'name',
            value: {
              app_name: 'data_management',
              id: datafile.id,
              model_name: 'dataset',
              mode_type: 'DEFAULT',
              name: datafile.name,
            },
          },
          ...datafile,
        })),
      }))
    },
    archivableDatafiles() {
      return this.datafiles?.filter(
        (datafile) =>
          this.datafilesStatusById[datafile.id].archivable &&
          !(
            datafile.archives.length > 0 && this.archiveInfo.ignoreArchivedFiles
          ),
      )
    },
    datafilesStatusById() {
      return this.datafiles.reduce((datafilesStatusById, datafile) => {
        datafilesStatusById[datafile.id] = {
          archivable: datafile.exists && datafile.status === 'ONLINE',
          ...this.getNonArchivableReason(datafile),
        }

        return datafilesStatusById
      }, {})
    },
    datasetsArchivableById() {
      return this.displayedDatafilesInfo.reduce((datasetArchivable, data) => {
        datasetArchivable[data.dataset.value.id] = !data.datafiles.every(
          (datafile) =>
            !this.datafilesStatusById[datafile.id].archivable ||
            (this.archiveInfo.ignoreArchivedFiles && datafile.archives.length),
        )
        return datasetArchivable
      }, {})
    },
    showErrorTicker() {
      const archiveStatus = Object.values(this.datafilesStatusById)
      if (archiveStatus.some((datafile) => datafile.icon?.includes('bug'))) {
        return 'You have missing datafiles, please contact your admin'
      } else if (this.datafiles.length < 1) {
        return 'No datafiles associated with this dataset!'
      } else if (this.archivableDatafiles.length < 1) {
        return 'No datafiles can be archived!'
      }

      return null
    },
    totalArchivableSize() {
      return this.archivableDatafiles.reduce(
        (size, datafile) => (size += datafile.size || 0),
        0,
      )
    },
    showRelatedObjects() {
      const allowedRelatedObjectsModel = ['projects', 'studies']
      return allowedRelatedObjectsModel.includes(this.modelName)
    },
    studiesAssociatedToSelectedProjects() {
      return this.selectedItems?.[0].studies && this.modelName === 'projects'
        ? [
            ...new Set(
              this.selectedItems.reduce((studies, item) => {
                return [
                  ...studies,
                  ...(item?.studies?.value?.map((study) =>
                    JSON.stringify({ ...item?.studies, value: study }),
                  ) || []),
                ]
              }, []),
            ),
          ].map((study) => JSON.parse(study))
        : []
    },
    buttonTooltipMessage() {
      if (this.showErrorTicker) {
        return this.showErrorTicker
      } else if (!this.archiveInfo.name) {
        return 'Please enter an archive name!'
      } else if (!this.archiveInfo.billingDetails) {
        return 'Please enter the billing information!'
      } else if (!this.archiveConfirmation) {
        return 'Please read the detail information and confirm by clicking the checkbox!'
      } else return null
    },
    selectedItemsPanelIsOpened() {
      return this.selectedItems.some((item) => item.archives.value.length)
    },
    selectedItemsLabel() {
      return `${this.selectedItemsCount} ${pluralize(
        capitalize(this.modelName),
        this.selectedItemsCount,
      )}`
    },
    studiesLabel() {
      return `${this.studiesAssociatedToSelectedProjects.length} ${pluralize(
        'Study',
        this.studiesAssociatedToSelectedProjects.length,
      )}`
    },
    datasetsLabel() {
      return `${this.displayedDatafilesInfo.length} ${pluralize(
        'Dataset',
        this.displayedDatafilesInfo.length,
      )}`
    },
    relatedObjectsArchiveStatusById() {
      const datafilesGroupedByRelatedObject = new Map()
      this.datafiles.forEach((datafile) => {
        datafile[this.modelName].forEach((project) => {
          if (datafilesGroupedByRelatedObject.has(project.id)) {
            datafilesGroupedByRelatedObject.set(project.id, [
              ...datafilesGroupedByRelatedObject.get(project.id),
              datafile,
            ])
          } else {
            datafilesGroupedByRelatedObject.set(project.id, [datafile])
          }
        })
      })

      const relatedObjectsArchiveStatusById = {}
      for (let [projectId, datafiles] of datafilesGroupedByRelatedObject) {
        relatedObjectsArchiveStatusById[projectId] = datafiles.every(
          (datafile) => datafile.archives.length > 0,
        )
          ? 'All'
          : 'Some'
      }

      return relatedObjectsArchiveStatusById
    },
  },
  methods: {
    pluralize,
    prettySize,
    closeNotification() {
      delete this.archiveResponse['']
    },
    validateForm() {
      if (this.archiveInfo.name && this.archiveInfo.billingDetails) return true
      else {
        this.$store.commit('notify', {
          type: 'error',
          message: 'Please complete the archive-information form!',
        })
      }

      return false
    },
    getNonArchivableReason(datafile) {
      if (!datafile.exists && !datafile.archives.length) {
        return {
          reason:
            'The datafile is not being archived and also it is not in the disk, please contact your admin',
          icon: 'fa-solid fa-bug',
        }
      } else if (!datafile.exists) {
        return {
          reason: 'Datafile does not exist on disk!',
          icon: 'fa-regular fa-file-excel',
        }
      }

      return null
    },
    handleArchive() {
      if (!this.validateForm()) return

      this.isArchiving = true

      const payload = this.preparePayloadForArchiving()
      const url = 'data_management/archives/'
      post(url, { data: payload })
        .then((res) => {
          this.$store.commit('notify', {
            type: 'success',
            message: 'Successfully created archive job!',
          })

          this.$router.push({
            path: `/${res.data.results.name.value.id}`,
          })
        })
        .catch((error) => {
          this.$store.commit('notify', {
            type: 'error',
            message:
              error.response.data.message || 'Error creating archive task.',
          })
        })
        .finally(() => {
          this.isArchiving = false
        })
    },
    preparePayloadForArchiving() {
      return {
        name: this.archiveInfo.name,
        description: this.archiveInfo.description,
        owner: this.user.username,
        group: this.user.primaryGroup.id,
        billing_details: this.archiveInfo.billingDetails,
        related_objects: this.getRelatedObjectsOfArchivableItems(),
        datafilecopies: this.archivableDatafiles.map(({ id }) => id),
        ...(this.archiveInfo.ignoreArchivedFiles && {
          note: `Previously archived datafiles are not part of this archive, as requested by ${this.user.fullName}`,
        }),
      }
    },
    getRelatedObjectsOfArchivableItems() {
      const relatedObjects = []
      if (['projects', 'studies'].includes(this.modelName)) {
        this.selectedItems.forEach(({ id }) => relatedObjects.push(id.value))
        this.studiesAssociatedToSelectedProjects.forEach(({ value }) =>
          relatedObjects.push(value.id),
        )
      }

      return relatedObjects
    },
    groupByDataset(datafiles) {
      const datafilesGroupedByDataset = new Map()
      datafiles.forEach((datafile) => {
        datafile.datasets.forEach((dataset) => {
          if (datafilesGroupedByDataset.has(dataset.id)) {
            const datafileGroup = datafilesGroupedByDataset.get(dataset.id)
            datafilesGroupedByDataset.set(dataset.id, {
              ...datafileGroup,
              datafiles: [...datafileGroup.datafiles, datafile],
            })
          } else {
            datafilesGroupedByDataset.set(dataset.id, {
              dataset,
              datafiles: [datafile],
            })
          }
        })
      })

      return datafilesGroupedByDataset
    },
    injectDescription() {
      const description = `In this archive, we have ${
        this.archivableDatafiles.length
      } datafiles of size - ${this.prettySize(
        this.totalArchivableSize,
      )} from the following

      ${capitalize(this.modelName)}:
      ${this.selectedItems.reduce((text, item, index) => {
        if (
          typeof item.number_of_datasets === 'undefined' ||
          item?.number_of_datasets?.value > 0
        ) {
          text += `\t${index + 1}. ${item.name.value.name} - ${
            location.host
          }${baseUrl}${item.name.value.id}\n`
        }
        return text
      }, '')}
      ${
        this.studiesAssociatedToSelectedProjects.length
          ? `Studies:
      ${this.studiesAssociatedToSelectedProjects.reduce(
        (text, study, index) => {
          text += `\t${index + 1}. ${study.value.name} - ${location.host}/${
            study.value.id
          }\n`
          return text
        },
        '',
      )}`
          : ''
      }`

      this.archiveInfo.description = description
    },
  },
  watch: {
    archivableDatafiles: {
      handler() {
        this.injectDescription()
      },
      deep: true,
    },
    archiveInfo: {
      handler(newVal, oldVal) {
        if (newVal.ignoreArchivedFiles !== oldVal.ignoreArchivedFiles) {
          this.datasetsArchivableById()
        }
      },
      deep: true,
    },
  },
  created() {
    setTimeout(() => {
      this.showLoaderHelpText = true
    }, 2500)
  },
}
</script>

<style scoped>
.batcharchive {
  padding: 0 0.5rem;
}

.batcharchive,
.batcharchive-content {
  height: 100%;
}

.batcharchive-content {
  display: flex;
}

.batcharchive-content {
  flex-direction: column;
}

.batcharchive-content.loading {
  justify-content: center;
  align-items: center;
}

.batcharchive-notifications .notification {
  margin-bottom: 0.2rem;
}

.content-item-list-container {
  background-color: var(--color-background-gray);
  max-height: 350px;
  overflow: auto;
}

.status-container {
  position: relative;
}

.item-status-icon {
  flex-grow: 0;
  flex-shrink: 0;
}

.item-status-icon.left {
  position: absolute;
  left: 0;
  top: -12px;
}
.item-status-icon.right {
  position: absolute;
  right: 0;
  top: -12px;
}

.list-item .item-name {
  flex-grow: 1;
}

.table-cell {
  display: inline-block;
  /* height: 1.4em; */
  max-width: fit-content;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sticky {
  position: sticky;
  background-color: #fafafa;
}
.at-top {
  top: 0;
  z-index: 9;
}
.at-bottom {
  bottom: 0;
  z-index: 9;
}
.archivable-sticky-col {
  position: sticky;
  right: 0;
  max-width: 100px;
  min-width: 100px;
}
.status-sticky-col {
  position: sticky;
  right: 100px;
  max-width: 80px;
  min-width: 80px;
}
.size-sticky-col {
  position: sticky;
  right: 180px;
  max-width: 80px;
  min-width: 80px;
}
.total-size-sticky {
  position: sticky;
  left: 0;
}

.content-item-list-container table {
  border-collapse: separate;
}

.batcharchive-notifications .notification:last-child {
  margin-bottom: 1rem;
}

.related-object-container {
  border: 1px solid #dbdbdb;
  padding: 10px 35px;
  max-height: 145px;
  overflow-y: auto;
}
.related-object-container li:first-child {
  margin-top: 5px;
}
.related-object-container li {
  margin-bottom: 8px;
}
.related-object-container li:last-child {
  margin-bottom: 12px;
}
.non-archivable {
  opacity: 0.3;
  filter: grayscale(1);
}
.dataset-col {
  word-break: break-word;
  min-width: 200px;
}
</style>
